import type { FunctionComponent } from 'react';
import { useCallback, useState } from 'react';

import type { IPaymentProcessResponse } from '@endeavour/payment-integration';

import { Button, ButtonSize, Rounding } from '../../../shared/components/button/button';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { Loader } from '../../../shared/components/loader/loader';
import { createStyleHelper, createClassName } from '../../../shared/utils/class-names';
import { useResponseHandler } from '../../hooks/use-response-handler';

import styles from './window-popup.module.scss';

const style = createStyleHelper(styles, 'window-popup');

export interface IWindowPopupProps {
    className?: string;
    responseModel: IPaymentProcessResponse;
}

export const WindowPopup: FunctionComponent<IWindowPopupProps> = ({ className, responseModel }) => {
    const [isLoading, setIsLoading] = useState(false);
    const output = responseModel.redirectOutput;

    const onPopupOpen = useCallback(() => {
        const height = output?.height ?? 700;
        const width = output?.width ?? 800;
        const options: Record<string, number> = {
            height,
            left: innerWidth / 2 - width / 2,
            top: innerHeight / 2 - height / 2,
            width,
        };
        const popup = window.open(
            output?.url ? output.url : 'about:blank',
            '_blank',
            Object.keys(options)
                .reduce((acc, curr) => {
                    acc.push([curr, options[curr]].join('='));
                    return acc;
                }, [] as string[])
                .join(','),
        );

        if (!popup) return;

        setIsLoading(true);

        if (output?.html) {
            popup.document.write(output.html);
        }
    }, [output]);

    useResponseHandler(responseModel.transactionId);

    return (
        <div className={createClassName([className, style()])}>
            {isLoading ? (
                <>
                    <Loader />
                    {/*
                        TODO: If you have closed the window by mistake, please click on this link....
                    */}
                </>
            ) : (
                <Button
                    className={style('button')}
                    size={ButtonSize.SMALL}
                    rounding={Rounding.SMALL}
                    onClick={() => onPopupOpen()}
                >
                    <FormattedMessage
                        id={'cashier.popup.click-to-proceed'}
                        defaultMessage={'Click to proceed'}
                    />
                </Button>
            )}
        </div>
    );
};
